import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudFog, CloudSnow, CloudLightning, Sunset, Flower, ChevronRight } from "lucide-react";
import axios from "axios";
import { tr } from "../lib/i18n";
import { R, weatherLabel } from "../lib/roomI18n";

const ICONS = { sun: Sun, cloud: Cloud, "cloud-sun": CloudSun, rain: CloudRain, drizzle: CloudDrizzle, fog: CloudFog, snow: CloudSnow, storm: CloudLightning };

let _weatherPromise = null;
function useRoomWeather() {
  const [w, setW] = useState(null);
  useEffect(() => {
    if (!_weatherPromise) {
      _weatherPromise = axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/rooms-weather`).then((r) => r.data).catch(() => null);
    }
    _weatherPromise.then(setW);
  }, []);
  return w;
}

// Today's weather + sunset chip shown under the greeting in the room page.
export function RoomWeather({ lang = "it" }) {
  const w = useRoomWeather();
  if (!w) return null;
  const Icon = ICONS[w.icon] || Sun;
  const label = weatherLabel(w.icon, lang, w.label);
  return (
    <div className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-full bg-white border border-[#e8dcc8] px-3 py-1.5 text-xs text-[#4a1c17]" data-testid="room-weather">
      {w.temp !== null && w.temp !== undefined && (
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <Icon className="h-4 w-4 text-[#C48B31]" /> {w.temp}°{label ? ` · ${label}` : ""}
        </span>
      )}
      {w.tmin !== null && w.tmax !== null && w.tmin !== undefined && (
        <span className="text-[#7c5a4e]">{tr(R.min, lang)} {w.tmin}° · {tr(R.max, lang)} {w.tmax}°</span>
      )}
      <span className="inline-flex items-center gap-1 text-[#7c5a4e]"><Sunset className="h-3.5 w-3.5" /> {tr(R.sunset, lang)} {w.sunset}</span>
    </div>
  );
}

// Tomorrow-based suggestion: rain → Spa, clear sky → sunset horse ride.
export function RoomWeatherTip({ lang = "it" }) {
  const navigate = useNavigate();
  const w = useRoomWeather();
  const t = w?.tomorrow;
  if (!t?.tip) return null;
  const spa = t.tip === "spa";
  const label = weatherLabel(t.icon, lang, t.label);
  return (
    <button
      type="button"
      onClick={() => navigate(spa ? "/spa" : "/cavallo")}
      data-testid={`room-weather-tip-${t.tip}`}
      className="flex w-full items-center gap-3 rounded-2xl border border-[#e8dcc8] bg-white px-4 py-3 text-left shadow-sm hover:bg-[#faf3ee] active:scale-[0.99] transition-[background-color,transform]"
    >
      <span className="h-10 w-10 shrink-0 rounded-full bg-[#4a1c17] text-[#f7ebdf] flex items-center justify-center">
        {spa ? <Flower className="h-5 w-5" /> : <Sunset className="h-5 w-5" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs text-[#7c5a4e]">{tr(R.tomorrow, lang)} {label?.toLowerCase()}{t.tmax !== null && t.tmax !== undefined ? ` · ${tr(R.max, lang)} ${t.tmax}°` : ""}</span>
        <span className="block text-sm font-semibold text-[#2b1410] leading-tight">
          {spa ? tr(R.tipSpa, lang) : tr(R.tipHorse, lang)}
        </span>
      </span>
      <ChevronRight className="h-4 w-4 text-[#7c5a4e] shrink-0" />
    </button>
  );
}
